import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";

// ---------------------------------------------------------------------------
// Firestore collection names
// ---------------------------------------------------------------------------
export const COLLECTIONS = {
  programs: "programs",
  instructors: "instructors",
  team: "team",
  news: "news",
  announcements: "announcements",
  registrations: "registrations",
  settings: "settings",
  admins: "admins",
};

// ---------------------------------------------------------------------------
// Cloudinary configuration
// ---------------------------------------------------------------------------
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

// ---------------------------------------------------------------------------
// Existing Website Baseline Data (Used for Seeding & Fallbacks)
// ---------------------------------------------------------------------------
export const INITIAL_WEBSITE_DATA = {
  programs: [
    {
      title: "Online Robotics Bootcamp",
      description: "A 1-year online program teaching robotics fundamentals, Arduino programming, and IoT projects.",
      status: "Open",
      programType: "upcoming",
      imageUrl: "/images/bootcamp2022.svg",
      order: 1,
    },
    {
      title: "Web Development Fundamentals",
      description: "Learn HTML, CSS, JavaScript, and React to build modern, interactive web applications.",
      status: "Open",
      programType: "upcoming",
      imageUrl: "/images/Custom Coding.svg",
      order: 2,
    },
    {
      title: "AI & Machine Learning Intro",
      description: "Introduction to artificial intelligence concepts, Python programming, and practical ML models.",
      status: "Open",
      programType: "upcoming",
      imageUrl: "/images/bootcamp2020.svg",
      order: 3,
    },
    {
      title: "Hardware Programming & Embedded Systems",
      description: "Hands-on training in microcontrollers, sensors, and circuit design to innovate with technology.",
      status: "Open",
      programType: "upcoming",
      imageUrl: "/images/ConnectionHardware.svg",
      order: 4,
    },
    {
      title: "UI/UX Design for Tech",
      description: "User interface and user experience design principles for building modern hardware and software systems.",
      status: "Open",
      programType: "upcoming",
      imageUrl: "/images/graphic design.svg",
      order: 5,
    },
    {
      title: "Training Session at Calvary Arrows",
      description: "Hands-on robotics and coding workshops conducted for students at Calvary Arrows College.\n\nStudents explored foundational electronics, sensor interfacing, and building real-world automated prototypes.",
      status: "Completed",
      programType: "completed",
      eventDate: "2019-11-15",
      imageUrl: "/images/in Calvary Arrows.svg",
      galleryImages: [
        "/images/bootcamp2020.svg",
        "/images/bootcamp2022.svg",
      ],
      order: 6,
    },
    {
      title: "Bootcamp 2020",
      description: "An intensive immersion program focusing on Python programming, algorithmic thinking, and practical hardware design.\n\nParticipants worked in teams to build functional STEM demonstration kits.",
      status: "Completed",
      programType: "completed",
      eventDate: "2020-08-20",
      imageUrl: "/images/bootcamp2020.svg",
      galleryImages: [
        "/images/in Calvary Arrows.svg",
      ],
      order: 7,
    },
    {
      title: "Bootcamp 2022",
      description: "Comprehensive youth empowerment bootcamp covering web development, embedded systems, and collaborative technology innovation.\n\nFeaturing project presentations, interactive mentoring, and award recognitions.",
      status: "Completed",
      programType: "completed",
      eventDate: "2022-09-10",
      imageUrl: "/images/bootcamp2022.svg",
      galleryImages: [
        "/images/bootcamp2020.svg",
      ],
      order: 8,
    },
  ],
  instructors: [
    {
      name: "Olubowale Gladys Opeoluwa",
      role: "Founder and CEO",
      bio: "Chemical Engineering and Mastercard Foundation scholar, University of Toronto, Canada Research Project Management Process Engineering Intern, Vale Ltd Canada.",
      availability: "Available",
      imageUrl: "/team/gladys.svg",
      order: 1,
    },
    {
      name: "Joshua Momo",
      role: "Operations Director / Program Director",
      bio: "Graduate Student in the MS Engineering AI program at CMU-Africa.",
      availability: "Available",
      imageUrl: "/team/josh.svg",
      order: 2,
    },
    {
      name: "Lesh Tayo",
      role: "UI/UX Designer",
      bio: "Computer science student at Ekiti State University, Nigeria.",
      availability: "Available",
      imageUrl: "/team/lesh.svg",
      order: 3,
    },
    {
      name: "Ekundayo Shallom",
      role: "Developer",
      bio: "Computer science student at University of Jos, Nigeria.",
      availability: "Available",
      imageUrl: "/team/shally.svg",
      order: 4,
    },
    {
      name: "Iseoluwa Gbotemi",
      role: "Developer",
      bio: "Computer science student at University of Toronto.",
      availability: "Available",
      imageUrl: "/team/issues.svg",
      order: 5,
    },
  ],
  team: [
    { name: "Olubowale Gladys", role: "Founder", bio: "Chemical Engineering and Mastercard Foundation scholar, University of Toronto", imageUrl: "/team/gladys.svg", order: 1 },
    { name: "Joshua Wisdom Momo", role: "Operations Director / Program Director", bio: "MS Engineering AI at CMU-Africa", imageUrl: "/team/josh.svg", order: 2 },
    { name: "Adesanya Deborah", role: "Community Manager / Content Writer", bio: "Content strategist and community builder", imageUrl: "/team/debby.svg", order: 3 },
    { name: "Stephen Adeniyi Deji", role: "Assistant Operations Director / Program Director", bio: "Program coordination and youth mentorship", imageUrl: "/team/deji_steven.svg", order: 4 },
    { name: "Nehemiah Amedu Odagboyi", role: "Human Resource Director", bio: "Talent recruitment and team management", imageUrl: "/team/nehhy.svg", order: 5 },
    { name: "Jedidiah Oladepo Ifeoluwa", role: "Graphic Designer", bio: "Visual branding and media creative", imageUrl: "/team/jeddy.svg", order: 6 },
    { name: "Ekundayo Shallom", role: "Developer", bio: "Software engineer and STEM mentor", imageUrl: "/team/shally.svg", order: 7 },
    { name: "Lesh Tayo", role: "UI/UX Designer", bio: "Product design and user experience specialist", imageUrl: "/team/lesh.svg", order: 8 },
    { name: "Iseoluwa Gbotemi", role: "Developer", bio: "Full stack engineering student at University of Toronto", imageUrl: "/team/issues.svg", order: 9 },
    { name: "Ariel Hne", role: "Community Manager / Content Writer", bio: "Advocacy and student community engagement", imageUrl: "/team/ariel.svg", order: 10 },
    { name: "Samuel Oghuwun", role: "Teen Coach - STEM", bio: "Hands-on robotics and youth technology instructor", imageUrl: "/team/sam.svg", order: 11 },
  ],
  news: [
    {
      title: "XploreStem Launches New Robotics Program",
      summary: "We are excited to announce a brand new 1-year online robotics bootcamp, proudly sponsored by XploreSTEMi with partners across Africa.",
      imageUrl: "/images/bootcamp2020.svg",
      date: "Recent Update",
    },
    {
      title: "Partnership with Women in Engineering",
      summary: "We are pleased to announce our strategic partnership with Women in Engineering to empower more female students and leaders in tech.",
      imageUrl: "/images/women.svg",
      date: "16th September 2021",
    },
    {
      title: "Calvary Arrows STEM Training Outreach",
      summary: "Our hands-on robotics and programming workshop in Calvary Arrows equipped secondary school students with foundational tech skills.",
      imageUrl: "/images/in Calvary Arrows.svg",
      date: "Past Event",
    },
  ],
  announcements: [
    {
      title: "Announcement",
      message: "It's Finally Here... \nOnline 1 Year Robotics bootcamp. \nProudly Sponsored By XploreSTEMi With Partners Across Africa.",
      ctaLabel: "Join Now!!!",
      ctaUrl: "/register",
    },
  ],
  settings: {
    registrationOpen: false,
    contactEmail: "info@explorestemi.com",
    contactPhone: "+1 (416) 523-0392",
    instagramHandle: "@xplorestemi",
    officeHours: "09:00 AM - 6:00 PM (Mon - Sat)",
    missionText: "To ignite a passion for STEM in primary and secondary school students from disadvantage backgrounds through hands-on learning in coding, robotics, and science, empowering them with diverse skills to solve real-world problems and contribute to the growth of developing nations.",
    visionText: "To create a future where students from diverse and underrepresented backgrounds in developing nations are equipped with STEM skills, driving innovation for technological advancement and sustainable development as leaders and problem-solvers in a thriving global community.",
    whatsappCommunityUrl: "https://chat.whatsapp.com/GbToN7d2xVN7RpDIXMrOLC",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const sortItems = (items) => {
  return [...items].sort((a, b) => {
    if (a.order != null && b.order != null && a.order !== b.order) {
      return Number(a.order) - Number(b.order);
    }
    const aTime = a.createdAt?.toMillis
      ? a.createdAt.toMillis()
      : a.createdAt?.seconds
      ? a.createdAt.seconds * 1000
      : a.createdAt
      ? new Date(a.createdAt).getTime()
      : 0;
    const bTime = b.createdAt?.toMillis
      ? b.createdAt.toMillis()
      : b.createdAt?.seconds
      ? b.createdAt.seconds * 1000
      : b.createdAt
      ? new Date(b.createdAt).getTime()
      : 0;
    return bTime - aTime;
  });
};

// ---------------------------------------------------------------------------
// Public fetchers (used across public pages)
// ---------------------------------------------------------------------------

export const getPrograms = async () => {
  try {
    const snap = await getDocs(collection(db, COLLECTIONS.programs));
    if (snap.empty) return INITIAL_WEBSITE_DATA.programs;
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return sortItems(items);
  } catch (err) {
    console.warn("Firestore getPrograms fallback:", err);
    return INITIAL_WEBSITE_DATA.programs;
  }
};

export const getInstructors = async () => {
  try {
    const snap = await getDocs(collection(db, COLLECTIONS.instructors));
    if (snap.empty) return INITIAL_WEBSITE_DATA.instructors;
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return sortItems(items);
  } catch (err) {
    console.warn("Firestore getInstructors fallback:", err);
    return INITIAL_WEBSITE_DATA.instructors;
  }
};

export const getTeam = async () => {
  try {
    const snap = await getDocs(collection(db, COLLECTIONS.team));
    if (snap.empty) return INITIAL_WEBSITE_DATA.team;
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return sortItems(items);
  } catch (err) {
    console.warn("Firestore getTeam fallback:", err);
    return INITIAL_WEBSITE_DATA.team;
  }
};

export const getNewsItems = async () => {
  try {
    const snap = await getDocs(collection(db, COLLECTIONS.news));
    if (snap.empty) return INITIAL_WEBSITE_DATA.news;
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return sortItems(items);
  } catch (err) {
    console.warn("Firestore getNewsItems fallback:", err);
    return INITIAL_WEBSITE_DATA.news;
  }
};

export const getLatestAnnouncement = async () => {
  try {
    const snap = await getDocs(collection(db, COLLECTIONS.announcements));
    if (snap.empty) return INITIAL_WEBSITE_DATA.announcements[0];
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const sorted = sortItems(items);
    return sorted[0] || INITIAL_WEBSITE_DATA.announcements[0];
  } catch (err) {
    console.warn("Firestore getLatestAnnouncement fallback:", err);
    return INITIAL_WEBSITE_DATA.announcements[0];
  }
};

export const getSiteSettings = async () => {
  try {
    const docRef = doc(db, COLLECTIONS.settings, "general");
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return { ...INITIAL_WEBSITE_DATA.settings, ...snap.data() };
    }
    return INITIAL_WEBSITE_DATA.settings;
  } catch (err) {
    console.warn("Firestore getSiteSettings fallback:", err);
    return INITIAL_WEBSITE_DATA.settings;
  }
};

export const subscribeToSiteSettings = (callback) => {
  try {
    const docRef = doc(db, COLLECTIONS.settings, "general");
    return onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          callback({ ...INITIAL_WEBSITE_DATA.settings, ...snap.data() });
        } else {
          callback(INITIAL_WEBSITE_DATA.settings);
        }
      },
      (err) => {
        console.warn("Settings realtime subscription error:", err);
        callback(INITIAL_WEBSITE_DATA.settings);
      }
    );
  } catch (err) {
    console.warn("Unable to subscribe to site settings:", err);
    callback(INITIAL_WEBSITE_DATA.settings);
    return () => {};
  }
};

export const updateSiteSettings = async (settingsPayload) => {
  const docRef = doc(db, COLLECTIONS.settings, "general");
  await setDoc(docRef, { ...settingsPayload, updatedAt: serverTimestamp() }, { merge: true });
  const snap = await getDoc(docRef);
  return snap.data();
};

// ---------------------------------------------------------------------------
// Admin CRUD (content management)
// ---------------------------------------------------------------------------

export const listContent = async (section) => {
  const col = COLLECTIONS[section];
  if (!col) return [];
  try {
    const snap = await getDocs(collection(db, col));
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return sortItems(items);
  } catch (err) {
    console.warn(`Failed to list ${section}:`, err);
    return [];
  }
};

export const createContent = async (section, payload) => {
  const col = COLLECTIONS[section];
  if (!col) throw new Error(`Unknown section: ${section}`);
  const docRef = await addDoc(collection(db, col), {
    ...payload,
    createdAt: serverTimestamp(),
  });
  const snap = await getDoc(docRef);
  return { id: snap.id, ...snap.data() };
};

export const updateContent = async (section, id, payload) => {
  const col = COLLECTIONS[section];
  if (!col) throw new Error(`Unknown section: ${section}`);
  const docRef = doc(db, col, id);
  await updateDoc(docRef, { ...payload, updatedAt: serverTimestamp() });
  const snap = await getDoc(docRef);
  return { id: snap.id, ...snap.data() };
};

export const deleteContent = async (section, id) => {
  const col = COLLECTIONS[section];
  if (!col) throw new Error(`Unknown section: ${section}`);
  await deleteDoc(doc(db, col, id));
};

// ---------------------------------------------------------------------------
// Image upload (Cloudinary — unsigned upload)
// ---------------------------------------------------------------------------

// Validation constants for image uploads
export const IMAGE_VALIDATION = {
  maxSizeBytes: 5 * 1024 * 1024, // 5 MB
  maxSizeMB: 5,
  maxGalleryImages: 10,
  acceptedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
  acceptedExtensions: ".jpg,.jpeg,.png,.webp,.gif",
  acceptString: "image/jpeg,image/png,image/webp,image/gif",
};

/**
 * Validates a single image file for type and size.
 * Returns an error message string if invalid, or null if valid.
 */
export const validateImageFile = (file) => {
  if (!file) return "No file provided.";
  if (!IMAGE_VALIDATION.acceptedTypes.includes(file.type)) {
    return `Unsupported file type "${file.type}". Accepted: JPEG, PNG, WebP, GIF.`;
  }
  if (file.size > IMAGE_VALIDATION.maxSizeBytes) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return `File size ${sizeMB} MB exceeds the ${IMAGE_VALIDATION.maxSizeMB} MB limit.`;
  }
  return null;
};

export const uploadImage = async (file) => {
  if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
    throw new Error("Cloudinary is not configured. Set REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET in .env");
  }

  // Validate before uploading
  const validationError = validateImageFile(file);
  if (validationError) throw new Error(validationError);

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    { method: "POST", body: formData }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error?.message || "Image upload failed");
  }

  const data = await response.json();
  return data.secure_url;
};

/**
 * Upload multiple image files to Cloudinary.
 * Returns an array of uploaded image URLs.
 * Stops and throws on the first failure.
 */
export const uploadMultipleImages = async (files) => {
  if (!Array.isArray(files) || files.length === 0) return [];
  if (files.length > IMAGE_VALIDATION.maxGalleryImages) {
    throw new Error(`Maximum ${IMAGE_VALIDATION.maxGalleryImages} gallery images allowed.`);
  }
  // Validate all files first before uploading any
  for (const file of files) {
    const err = validateImageFile(file);
    if (err) throw new Error(`"${file.name}": ${err}`);
  }
  const urls = [];
  for (const file of files) {
    const url = await uploadImage(file);
    urls.push(url);
  }
  return urls;
};

// ---------------------------------------------------------------------------
// Registration management
// ---------------------------------------------------------------------------

export const submitRegistration = async (data) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.registrations), {
    ...data,
    reviewStatus: "Pending",
    createdAt: serverTimestamp(),
  });
  return { id: docRef.id };
};

export const subscribeToRegistrations = (callback) => {
  const q = query(collection(db, COLLECTIONS.registrations), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(items);
  });
};

export const getRegistrations = async () => {
  const q = query(collection(db, COLLECTIONS.registrations), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const updateRegistrationStatus = async (id, status) => {
  const docRef = doc(db, COLLECTIONS.registrations, id);
  await updateDoc(docRef, { reviewStatus: status, updatedAt: serverTimestamp() });
};

export const deleteRegistration = async (id) => {
  const docRef = doc(db, COLLECTIONS.registrations, id);
  await deleteDoc(docRef);
};

// ---------------------------------------------------------------------------
// Seeder / Migration for Existing Website Data
// ---------------------------------------------------------------------------

export const seedExistingData = async (force = false) => {
  const results = {
    programs: 0,
    instructors: 0,
    team: 0,
    news: 0,
    announcements: 0,
    settings: 0,
  };

  const sectionsToSeed = ["programs", "instructors", "team", "news", "announcements"];

  for (const section of sectionsToSeed) {
    const colRef = collection(db, COLLECTIONS[section]);
    const existingSnap = await getDocs(colRef);
    const existingDocs = existingSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    const baselineItems = INITIAL_WEBSITE_DATA[section] || [];

    for (const item of baselineItems) {
      // Find existing match by title or name
      const match = existingDocs.find(
        (e) =>
          (item.name && e.name && e.name.toLowerCase().trim() === item.name.toLowerCase().trim()) ||
          (item.title && e.title && e.title.toLowerCase().trim() === item.title.toLowerCase().trim())
      );

      if (match) {
        // If match exists and has no imageUrl, backfill it
        if (!match.imageUrl && item.imageUrl) {
          await updateDoc(doc(db, COLLECTIONS[section], match.id), {
            imageUrl: item.imageUrl,
          });
          results[section]++;
        }
      } else {
        // Document does not exist in Firestore yet, add it
        await addDoc(colRef, {
          ...item,
          createdAt: serverTimestamp(),
        });
        results[section]++;
      }
    }
  }

  // Seed site settings if not present
  const settingsRef = doc(db, COLLECTIONS.settings, "general");
  const settingsSnap = await getDoc(settingsRef);
  if (!settingsSnap.exists()) {
    await setDoc(settingsRef, {
      ...INITIAL_WEBSITE_DATA.settings,
      updatedAt: serverTimestamp(),
    });
    results.settings = 1;
  } else if (force) {
    // Merge baseline defaults without overwriting customized contact info
    const currentData = settingsSnap.data() || {};
    await setDoc(
      settingsRef,
      {
        ...INITIAL_WEBSITE_DATA.settings,
        ...currentData,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  return results;
};

// ---------------------------------------------------------------------------
// Administrator & Role-Based Access Management
// ---------------------------------------------------------------------------

export const ROOT_ADMIN_EMAILS = [
  "ukanet4christ@gmail.com",
  (process.env.REACT_APP_INITIAL_ADMIN_EMAIL || "").toLowerCase().trim(),
].filter(Boolean);

export const verifyOrInitAdmin = async (user) => {
  if (!user || !user.email) {
    return { isAuthorized: false, role: null };
  }

  const normalizedEmail = user.email.toLowerCase().trim();
  const isRoot = ROOT_ADMIN_EMAILS.includes(normalizedEmail);

  try {
    const adminDocRef = doc(db, COLLECTIONS.admins, normalizedEmail);

    // 1. Try reading the specific user's document
    let adminSnap = null;
    try {
      adminSnap = await getDoc(adminDocRef);
    } catch (docErr) {
      console.warn("Could not read admin document directly:", docErr?.message);
    }

    if (adminSnap && adminSnap.exists()) {
      const data = adminSnap.data();
      return {
        isAuthorized: true,
        role: data.role || (isRoot ? "Super Admin" : "Admin"),
        record: { id: adminSnap.id, ...data },
      };
    }

    // 2. If document doesn't exist, check if user is root admin or if admins collection is empty
    let shouldBootstrap = isRoot;
    if (!shouldBootstrap) {
      try {
        const snap = await getDocs(collection(db, COLLECTIONS.admins));
        if (snap.empty) {
          shouldBootstrap = true;
        }
      } catch (colErr) {
        console.warn("Could not query admins collection:", colErr?.message);
      }
    }

    if (shouldBootstrap) {
      const initialData = {
        email: normalizedEmail,
        role: "Super Admin",
        name: user.displayName || normalizedEmail.split("@")[0] || "Primary Admin",
        addedBy: isRoot ? "root_config" : "system_init",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      try {
        await setDoc(adminDocRef, initialData);
      } catch (writeErr) {
        console.warn("Could not write initial admin document to Firestore (rules may need update):", writeErr?.message);
      }
      return { isAuthorized: true, role: "Super Admin", record: initialData };
    }

    // Not found in admins collection
    return { isAuthorized: false, role: null };
  } catch (err) {
    console.error("Error verifying admin access:", err);
    // If it is the root administrator, guarantee access even if Firestore rules failed
    if (isRoot) {
      return {
        isAuthorized: true,
        role: "Super Admin",
        record: { email: normalizedEmail, role: "Super Admin", name: "Primary Admin" },
      };
    }
    throw err;
  }
};

export const listAdmins = async () => {
  try {
    const snap = await getDocs(collection(db, COLLECTIONS.admins));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Ensure root admin is present in list
    ROOT_ADMIN_EMAILS.forEach((rootEmail) => {
      if (!list.some((a) => a.email?.toLowerCase() === rootEmail)) {
        list.unshift({
          id: rootEmail,
          email: rootEmail,
          role: "Super Admin",
          name: rootEmail.split("@")[0],
          addedBy: "System Config",
        });
      }
    });

    return list;
  } catch (err) {
    console.warn("Failed to list admins:", err);
    return ROOT_ADMIN_EMAILS.map((rootEmail) => ({
      id: rootEmail,
      email: rootEmail,
      role: "Super Admin",
      name: rootEmail.split("@")[0],
      addedBy: "System Config",
    }));
  }
};

export const addAdminUser = async ({ email, role = "Admin", name = "" }, actorEmail = "") => {
  if (!email || !email.includes("@")) {
    throw new Error("Please provide a valid email address.");
  }

  const normalizedEmail = email.toLowerCase().trim();
  const docRef = doc(db, COLLECTIONS.admins, normalizedEmail);
  const existing = await getDoc(docRef);

  if (existing.exists()) {
    throw new Error(`An administrator with email "${normalizedEmail}" already exists.`);
  }

  const adminPayload = {
    email: normalizedEmail,
    role: role === "Super Admin" ? "Super Admin" : "Admin",
    name: name.trim() || normalizedEmail.split("@")[0],
    addedBy: actorEmail || "Super Admin",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(docRef, adminPayload);
  return { id: normalizedEmail, ...adminPayload };
};

export const updateAdminRole = async (email, newRole, actorEmail = "") => {
  if (!email) throw new Error("Missing email.");
  const normalizedEmail = email.toLowerCase().trim();
  const docRef = doc(db, COLLECTIONS.admins, normalizedEmail);

  if (newRole !== "Super Admin") {
    const allAdmins = await listAdmins();
    const target = allAdmins.find((a) => a.email?.toLowerCase() === normalizedEmail);
    if (target?.role === "Super Admin") {
      const superAdminCount = allAdmins.filter((a) => a.role === "Super Admin").length;
      if (superAdminCount <= 1) {
        throw new Error("Cannot demote the only Super Admin. Please make another administrator Super Admin first.");
      }
    }
  }

  await updateDoc(docRef, {
    role: newRole === "Super Admin" ? "Super Admin" : "Admin",
    updatedBy: actorEmail,
    updatedAt: serverTimestamp(),
  });
};

export const removeAdminUser = async (email, currentActorEmail = "") => {
  if (!email) throw new Error("Missing email.");
  const normalizedEmail = email.toLowerCase().trim();

  // Safety check: Don't allow removing the last Super Admin
  const allAdmins = await listAdmins();
  const target = allAdmins.find((a) => a.email?.toLowerCase() === normalizedEmail);
  if (target?.role === "Super Admin") {
    const superAdminCount = allAdmins.filter((a) => a.role === "Super Admin").length;
    if (superAdminCount <= 1) {
      throw new Error("Cannot remove the only Super Admin. Please promote another admin to Super Admin first.");
    }
  }

  const docRef = doc(db, COLLECTIONS.admins, normalizedEmail);
  await deleteDoc(docRef);
};

